/**
 * Section + SectionHeading — the shared vertical-rhythm primitives.
 *
 * Editorial stack: optional mono kicker → display title → quiet meta line.
 * Every page band should be wrapped in <Section> so spacing, headings and
 * hairlines stay identical across the app.
 */
import { type ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

interface SectionHeadingProps {
  title: string;
  /** Small mono label above the title (e.g. "Chapter 01"). */
  kicker?: string;
  description?: string;
  actionLabel?: string;
  actionTo?: string;
  onAction?: () => void;
  icon?: ReactNode;
  className?: string;
}

export function SectionHeading({
  title,
  kicker,
  description,
  actionLabel,
  actionTo,
  onAction,
  icon,
  className,
}: SectionHeadingProps) {
  const actionClass =
    "inline-flex items-center gap-0.5 rounded-full px-3 text-xs font-semibold text-primary hover:bg-primary/10 shrink-0 min-h-[32px] transition active:scale-[0.97]";
  const action = actionLabel ? (
    actionTo ? (
      <Link to={actionTo} className={actionClass}>
        {actionLabel}
        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
      </Link>
    ) : (
      <button type="button" onClick={onAction} className={actionClass}>
        {actionLabel}
        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    )
  ) : null;

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {kicker && (
        <p className="gd-kicker flex items-center gap-2">
          <span aria-hidden className="h-px w-6 bg-primary/50" />
          {kicker}
        </p>
      )}
      <div className="flex items-end gap-3">
        {icon && (
          <span className="mb-0.5 flex h-9 w-9 items-center justify-center rounded-2xl gd-well text-primary [&_svg]:h-4 [&_svg]:w-4">
            {icon}
          </span>
        )}
        <h2 className="font-outfit text-2xl sm:text-[28px] font-bold leading-tight tracking-tight text-foreground">
          {title}
        </h2>
        <span aria-hidden className="hidden sm:block flex-1 mb-2 h-px bg-gradient-to-r from-border to-transparent" />
        <span className="flex-1 sm:hidden" />
        {action}
      </div>
      {description && <p className="gd-meta max-w-prose">{description}</p>}
    </div>
  );
}

interface SectionProps {
  children: ReactNode;
  title?: string;
  kicker?: string;
  description?: string;
  actionLabel?: string;
  actionTo?: string;
  onAction?: () => void;
  icon?: ReactNode;
  gap?: "tight" | "normal";
  className?: string;
}

export function Section({
  children,
  title,
  kicker,
  description,
  actionLabel,
  actionTo,
  onAction,
  icon,
  gap = "normal",
  className,
}: SectionProps) {
  return (
    <section className={cn("flex flex-col", gap === "tight" ? "gap-3" : "gap-4", className)}>
      {title && (
        <SectionHeading
          title={title}
          kicker={kicker}
          description={description}
          actionLabel={actionLabel}
          actionTo={actionTo}
          onAction={onAction}
          icon={icon}
        />
      )}
      {children}
    </section>
  );
}

export default Section;
